import { useEffect } from 'react'

import { useLazyQuery } from '@apollo/client'

import { GET_SITE_DIARY_SIGN_ONS } from '@/modules/siteDiary/graphQL'
import { GET_SUBCONTRACTORS_BY_PROJECT } from '@/modules/subcontractors/graphQL'
import { importWorkerAttendance } from '@/modules/templates/templateRenderer/CustomBlocks/WorkerAttendance/workerAttendanceHelpers'
import {
  AttendanceValue,
  HourConfig,
} from '@/modules/templates/types/blueprint'
import { getWorkerName, isProjectSelected } from '@/utils/index'

import {
  SiteDiarySignOnsUiQuery,
  SiteDiarySignOnsUiQueryVariables,
  Subcontractor,
  SubcontractorsByProjectUiQuery,
  SubcontractorsByProjectUiQueryVariables,
} from 'types/api.graphql'

type SiteDiarySignOns = SiteDiarySignOnsUiQuery['siteDiary']['signOns']
export type ManualSignOns = SiteDiarySignOns

type Options = {
  manuallySuppliedSubcontractors?: [Subcontractor]
  manuallySuppliedSignOns: ManualSignOns
  includeSubbieBreakdown?: boolean
  includeAutomatedSignOns?: boolean
  includeDeletedEntries?: boolean
}

export const useAttendanceData = (
  projectId: string,
  siteDiaryId: string,
  blockValue: AttendanceValue,
  hourConfig: HourConfig[],
  options: Options = {
    includeSubbieBreakdown: true,
    manuallySuppliedSignOns: null,
    manuallySuppliedSubcontractors: null,
    includeAutomatedSignOns: true,
    includeDeletedEntries: false,
  }
) => {
  const {
    includeSubbieBreakdown,
    manuallySuppliedSignOns,
    manuallySuppliedSubcontractors,
    includeAutomatedSignOns,
    includeDeletedEntries,
  } = options

  const [getSignOns, { data: siteDiaryData }] = useLazyQuery<
    SiteDiarySignOnsUiQuery,
    SiteDiarySignOnsUiQueryVariables
  >(GET_SITE_DIARY_SIGN_ONS, {
    variables: { id: siteDiaryId },
    fetchPolicy: 'cache-and-network',
  })

  const [getSubbies, { data: subcontractorData }] = useLazyQuery<
    SubcontractorsByProjectUiQuery,
    SubcontractorsByProjectUiQueryVariables
  >(GET_SUBCONTRACTORS_BY_PROJECT, {
    fetchPolicy: 'cache-and-network', // this is to ensure that the data is always up to date. PWA users had to close the app and reopen to force it to refresh
    variables: {
      projectId,
      filter: { includeBuilderSubcontractor: true },
    },
  })

  const hasVariables = !!siteDiaryId && !!projectId

  useEffect(() => {
    if (hasVariables) {
      if (!manuallySuppliedSignOns && includeAutomatedSignOns) {
        getSignOns()
      }
      if (includeSubbieBreakdown && isProjectSelected({ id: projectId })) {
        getSubbies()
      }
    }
  }, [
    hasVariables,
    getSignOns,
    getSubbies,
    projectId,
    manuallySuppliedSignOns,
    includeSubbieBreakdown,
    includeAutomatedSignOns,
  ])

  const signOnData =
    manuallySuppliedSignOns || siteDiaryData?.siteDiary?.signOns || []
  const subbieData = includeSubbieBreakdown ? subcontractorData : true

  const missingInputs = !siteDiaryId || !projectId
  const missingData = !subbieData || !signOnData

  if (missingInputs || missingData) {
    return {
      loading: true,
      projectWorkers: [],
      projectSubcontractors: [],
      importedAttendance: null,
    }
  }

  const importedAttendance = importWorkerAttendance(
    blockValue,
    signOnData,
    projectId,
    includeDeletedEntries
  )

  const projectSubcontractors =
    manuallySuppliedSubcontractors ||
    subcontractorData?.subcontractorsByProject ||
    []

  const projectWorkers = projectSubcontractors.reduce((acc, subbie) => {
    const workers = subbie.workers.map((w) => ({
      id: w.id,
      name: getWorkerName(w),
      subcontractorId: subbie.id,
    }))

    return [...acc, ...workers] // there will be duplicates but for different subcontractors
  }, []) as Array<{ id: string; name: string; subcontractorId: string }>

  return {
    loading: false,
    projectWorkers,
    projectSubcontractors,
    importedAttendance,
  }
}
